import React, { useState, useEffect } from "react";
import { func, string, object } from "prop-types";
import { connect } from "react-redux";
import get from "lodash/get";
import { parseUrl } from "query-string";
import { WithGoogleLogin, Link } from "@quintype/components";
import assetify from "@quintype/framework/assetify";

import Headline from "../../atoms/Headline";
import TextField from "../../atoms/TextField";
import PasswordField from "../../atoms/PasswordField";
import Button from "../../atoms/Button";
import MessageView from "../../atoms/MessageView";
import SocialAuthButton from "../../atoms/SocialAuthButton";
import WithSSO from "../../with-sso";
import Icon from "../../icons";
import Logo from "../../../../assets/images/palo-bangla.svg";
import BackIcon from "../../../../assets/images/back-icon.svg";

import { login } from "../../helper/api"; // eslint-disable-line
import { isValidEmail, getContinueToText } from "../../utils/utils";
import { setMember, setLoading } from "../../helper/actions";

import "./login.m.css";

const Login = ({
  setLoading,
  googleClientId,
  facebookId,
  currentPath,
  sketchesHost,
  authHost,
  publisherAttributes,
}) => {
  const [user, setUser] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isLoaderActive, setIsLoaderActive] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const params = parseUrl(currentPath);
  const redirectUrl = get(params, ["query", "redirect-url"], sketchesHost);
  const callbackUrl = get(params, ["query", "callback-url"], sketchesHost);
  const targetText = getContinueToText(callbackUrl);

  const setData = (e) => {
    const userObj = { ...user };
    const fieldName = e.target.name;
    userObj[fieldName] = e.target.value;

    // If error has been set then remove it
    if (e.target.value && userObj[`${fieldName}Error`]) {
      userObj[`${fieldName}Error`] = "";
    }

    // If error is set remove it since data is changed
    if (error) {
      setError(null);
    }

    // If success message is set remove it since data is changed
    if (successMessage) {
      setSuccessMessage(null);
    }

    setUser(userObj);
  };

  const onSubmitClick = (event) => {
    event.preventDefault();
    setIsLoaderActive(true);

    if (validate()) {
      setIsLoaderActive(false);
      // true -> field errors, false - no fields errors
      return;
    }

    const userObj = {
      username: user.email,
      password: user.password,
    };

    login(userObj, callbackUrl, redirectUrl, authHost)
      .then((response) => {
        setIsLoaderActive(false);
        window.location.href = response["redirect-url"];
      })
      .catch((err) => {
        setError(err.message);
        setIsLoaderActive(false);
      })
      .finally(setLoading(false));
  };

  const validate = () => {
    const updatedUser = { ...user };
    let flag = false;

    if (!updatedUser.email) {
      updatedUser.emailError = "Email address is required";
      flag = true;
    } else if (!isValidEmail(updatedUser.email)) {
      updatedUser.emailError = "Please enter a valid email address";
      flag = true;
    } else {
      updatedUser.emailError = "";
    }

    if (!updatedUser.password) {
      updatedUser.passwordError = "Please enter password";
      flag = true;
    } else {
      updatedUser.passwordError = "";
    }

    // If flag is true, there are field error's
    if (flag) {
      setUser(updatedUser); // update state with field errors
    }

    return flag;
  };

  const closeLoginPopup = () => {
    setShowLoginPopup(false);
  };

  const storePopupView = () => {
    localStorage.setItem("showPopup", false);
    setShowLoginPopup(false);
  };

  useEffect(() => {
    localStorage.getItem("showPopup") ? setShowLoginPopup(false) : setShowLoginPopup(true);
  }, []);

  useEffect(() => {
    showLoginPopup
      ? (global.document.body.style.overflow = "hidden")
      : (global.document.body.style.cssText = "overflow-x: hidden, overflow-y: auto");
  }, [showLoginPopup]);

  return (
    <form onSubmit={onSubmitClick}>
      <div styleName={`container ${publisherAttributes.lang}-container`}>
        {showLoginPopup && (
          <div styleName="login-popup-wrapper">
            <div styleName={`login-popup-container ${publisherAttributes.lang}-popup-container`}>
              <span onClick={closeLoginPopup}>
                <Icon type="close" className="close" />
              </span>
              <div>
                We have recently upgraded our system for better user experience and security. If you have registered on
                Prothom Alo before <b>20 August 2020</b> and logging for the first time, please use{" "}
                <Link
                  href={`forgot-password?redirect-url=${redirectUrl}&callback-url=${callbackUrl}`}
                  styleName="forgot-password-link"
                >
                  FORGOT PASSWORD
                </Link>{" "}
                option to set your password on new system
              </div>
              <div styleName="close-popup-button" onClick={storePopupView}>
                OK
              </div>
            </div>
            <div styleName="bg-overlay"></div>
          </div>
        )}
        <div styleName="wrapper">
          <a styleName="back-button" href={redirectUrl}>
            <img src={`${assetify(BackIcon)}`} alt="Back Icon" />
            <span styleName="back-text">Back</span>
          </a>
          <div styleName="content">
            <img src={`${assetify(Logo)}`} alt="Prothom Alo" styleName="palo-logo" />
            <Headline text="Login" headerLevel={3} headlineType={3} className={"login-text"} />
            <div styleName="continue-to">
              <Headline text="to continue to" headerLevel={4} headlineType={4} className={"continue-to-text"} />
              <Headline text={targetText} headerLevel={4} headlineType={4} className={"target-site"} />
            </div>
            <WithGoogleLogin
              sso={true}
              redirectUrl={redirectUrl}
              clientId={googleClientId}
              scope="email"
              emailMandatory
            >
              {({ login, serverSideLoginPath }) => (
                <SocialAuthButton
                  type="google"
                  className="google"
                  href={serverSideLoginPath}
                  lang={publisherAttributes.lang}
                />
              )}
            </WithGoogleLogin>
            <span styleName="or-text">Or</span>
            <TextField
              value={user.email}
              error={user.emailError}
              placeholder="Email"
              inputType="type2"
              name="email"
              onChange={setData}
            />
            <PasswordField
              value={user.password}
              error={user.passwordError}
              placeholder="Password"
              name="password"
              onChange={setData}
            />
            {error && (
              <MessageView
                type="error"
                text="Your email address or password is incorrect. Please check and try again"
              />
            )}
            <Button
              content="LOGIN"
              type="submit"
              buttonType="type3"
              isLoaderActive={isLoaderActive}
              selectedItem="login"
              buttonId="login"
            />
            <Link
              href={`forgot-password?redirect-url=${redirectUrl}&callback-url=${callbackUrl}`}
              styleName="forgot-password"
            >
              Forgot password
            </Link>
            <WithSSO ssoHost={authHost} signUpPath="/sign-up-page" redirectUrl={redirectUrl} callbackUrl={callbackUrl}>
              {({ signUpHref }) => (
                <span styleName="new-user">
                  New to Prothom Alo?
                  <Link href={signUpHref}>Create an account</Link>
                </span>
              )}
            </WithSSO>
          </div>
        </div>
        <div styleName="terms-privacy">
          By proceeding, you agree to{" "}
          <a styleName="terms-privacy-link" href={`${sketchesHost}/terms`}>
            Terms of Use
          </a>{" "}
          and{" "}
          <a styleName="terms-privacy-link" href={`${sketchesHost}/privacy-policy`}>
            Privacy Policy
          </a>{" "}
          of Prothom Alo
        </div>
      </div>
    </form>
  );
};

Login.propTypes = {
  setLoading: func,
  member: object,
  navigateToPage: func,
  setMember: func,
  checkForMemberUpdated: func,
  googleClientId: string,
  facebookId: string,
  setUser: func,
  currentPath: string,
  sketchesHost: string,
  authHost: string,
  publisherAttributes: object,
};

const mapStateToProps = (state) => ({
  googleClientId: get(state, ["qt", "config", "publisher-attributes", "socialAuth", "google"], ""),
  facebookId: get(state, ["qt", "config", "publisher-attributes", "socialAuth", "fb"], ""),
  member: state.member || null,
  sketchesHost: get(state, ["qt", "config", "sketches-host"], "https://www.prothomalo.com"),
  authHost: get(state, ["qt", "config", "publisher-attributes", "auth_host"], null),
  publisherAttributes: get(state, ["qt", "config", "publisher-attributes"], {}),
});

const mapDispatchToProps = (dispatch) => ({
  setLoading: (isLoading) => dispatch(setLoading(isLoading)),
  navigateToPage: (url) => global.app.navigateToPage(dispatch, url),
  setMember: (member) => dispatch(setMember(member)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Login);
